/**
 * The calendar: appointments, deadlines and approved leave in one month view.
 *
 * Three kinds of thing share it, and each is told apart by colour AND by its word, never
 * by colour alone: appointments (red, the brand), deadlines (amber) and absences
 * (green). The month grid says which days have something; the agenda beside it (below,
 * on a phone) says what, for the day picked.
 *
 * The page asks the API for the whole six-week grid rather than the month, so the grey
 * days at either end show their markers too and stepping to the next month does not
 * start from blank.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { Badge, Card, ErrorNotice, PageHeader, Spinner } from '@/components/ui';
import { EventForm } from '@/features/calendar/EventForm';
import {
  dayOfMonth,
  firstOfMonth,
  itemSpan,
  itemsByDay,
  monthGrid,
  monthOf,
  shiftMonth,
} from '@/features/calendar/days';
import { usePermissions } from '@/features/access/usePermissions';
import { useI18n, type TranslationKey } from '@/i18n';
import { api, type CalendarItem, type CalendarKind, type Section } from '@/lib/api';
import { dateLong, dateShort, intlTag, isoDate, time } from '@/lib/format';
import type { Tone } from '@/lib/labels';

const KIND_TONE: Record<CalendarKind, Tone> = {
  appointment: 'primary',
  deadline: 'warning',
  leave: 'success',
};

const DOT: Record<CalendarKind, string> = {
  appointment: 'bg-primary',
  deadline: 'bg-warning',
  leave: 'bg-success',
};

const CHIP: Record<CalendarKind, string> = {
  appointment: 'bg-[var(--color-primary-soft)] text-primary',
  deadline: 'bg-warning-soft text-warning',
  leave: 'bg-success-soft text-success',
};

const BAR: Record<CalendarKind, string> = {
  appointment: 'bg-primary',
  deadline: 'bg-warning',
  leave: 'bg-success',
};

/** Which permission a link's destination needs, so a link is only offered to someone who can follow it. */
function sectionForLink(link: string): Section | null {
  if (link.startsWith('/jobs')) return 'jobs';
  if (link.startsWith('/documents')) return 'documents';
  if (link.startsWith('/leave')) return 'leave';
  return null;
}

const KINDS: CalendarKind[] = ['appointment', 'deadline', 'leave'];

export function CalendarPage() {
  const { t, locale } = useI18n();
  const { can } = usePermissions();
  const today = isoDate(new Date());

  const [cursor, setCursor] = useState(() => monthOf(today));
  const [selected, setSelected] = useState(today);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<CalendarItem | null>(null);

  const config = useQuery({ queryKey: ['calendar-config'], queryFn: api.calendarConfig });
  const grid = useMemo(() => monthGrid(cursor.year, cursor.month), [cursor]);
  const data = useQuery({
    queryKey: ['calendar', grid.from, grid.to],
    queryFn: () => api.calendar(grid.from, grid.to),
    placeholderData: keepPreviousData,
  });

  const byDay = useMemo(
    () => itemsByDay(data.data?.items ?? [], grid.from, grid.to),
    [data.data, grid.from, grid.to],
  );
  const dayItems = byDay.get(selected) ?? [];

  const tag = intlTag(locale);
  const monthTitle = new Intl.DateTimeFormat(tag, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
    Date.UTC(cursor.year, cursor.month, 1),
  );
  // 1 January 2024 was a Monday, so seven consecutive days from it are Monday to Sunday.
  const weekdays = Array.from({ length: 7 }, (_, i) =>
    new Intl.DateTimeFormat(tag, { weekday: 'short', timeZone: 'UTC' }).format(Date.UTC(2024, 0, 1 + i)),
  );

  const go = (delta: number) => {
    const next = shiftMonth(cursor.year, cursor.month, delta);
    setCursor(next);
    setSelected(firstOfMonth(next.year, next.month));
  };
  const goToday = () => {
    setCursor(monthOf(today));
    setSelected(today);
  };
  const pick = (day: string) => {
    setSelected(day);
    const m = monthOf(day);
    if (m.year !== cursor.year || m.month !== cursor.month) setCursor(m);
  };

  const canAdd = config.data?.canAdd ?? false;
  const isOffice = config.data?.isOffice ?? false;

  return (
    <>
      <PageHeader
        title={t('calendar.title')}
        subtitle={t('calendar.subtitle')}
        actions={
          canAdd ? (
            <button type="button" className="btn-primary" onClick={() => setCreating(true)}>
              {t('calendar.add')}
            </button>
          ) : null
        }
      />

      <EventForm
        open={creating || editing !== null}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        event={editing}
        day={selected}
        isOffice={isOffice}
      />

      {data.isLoading ? (
        <Spinner />
      ) : data.error ? (
        <ErrorNotice error={data.error} />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] items-start">
          <Card padded={false}>
            <div className="flex items-center gap-2 p-3 border-b border-border">
              <h2 className="flex-1 font-bold capitalize truncate" aria-live="polite">
                {monthTitle}
              </h2>
              <button type="button" className="btn-secondary px-2.5" onClick={() => go(-1)} aria-label={t('calendar.previous')}>
                <Chevron direction="left" />
              </button>
              <button type="button" className="btn-secondary" onClick={goToday}>
                {t('calendar.today')}
              </button>
              <button type="button" className="btn-secondary px-2.5" onClick={() => go(1)} aria-label={t('calendar.next')}>
                <Chevron direction="right" />
              </button>
            </div>

            <div className="grid grid-cols-7 border-b border-border bg-surface-alt">
              {weekdays.map((w) => (
                <div key={w} className="py-1.5 text-center text-[11px] font-semibold uppercase tracking-wide text-subtle">
                  {w}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7">
              {grid.days.map((day, i) => {
                const items = byDay.get(day) ?? [];
                const inMonth = monthOf(day).month === cursor.month;
                const isSelected = day === selected;
                const kinds = KINDS.filter((k) => items.some((it) => it.kind === k));
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => pick(day)}
                    aria-pressed={isSelected}
                    aria-label={`${dateLong(day)}${items.length ? `: ${t(items.length === 1 ? 'calendar.itemsOne' : 'calendar.itemsMany', { count: items.length })}` : ''}`}
                    className={`relative min-h-[3.5rem] sm:min-h-[6.25rem] min-w-0 p-1 sm:p-1.5 text-start flex flex-col gap-1 border-b border-border transition-colors hover:bg-surface-alt ${
                      i % 7 !== 6 ? 'border-e' : ''
                    } ${isSelected ? 'bg-[var(--color-primary-soft)] ring-2 ring-inset ring-primary' : ''} ${
                      inMonth ? '' : 'bg-surface-alt/60'
                    }`}
                  >
                    <span
                      className={`inline-grid h-6 min-w-6 place-items-center rounded-full px-1 text-xs tabular ${
                        day === today
                          ? 'bg-primary text-[var(--color-on-primary)] font-bold'
                          : inMonth
                            ? 'font-semibold'
                            : 'text-subtle'
                      }`}
                    >
                      {dayOfMonth(day)}
                    </span>

                    {/* Dots on a phone, where a title would be three letters; titles from
                        `sm` up, where there is room for them. */}
                    <span className="flex gap-1 sm:hidden" aria-hidden>
                      {kinds.map((k) => (
                        <span key={k} className={`h-1.5 w-1.5 rounded-full ${DOT[k]}`} />
                      ))}
                    </span>
                    <span className="hidden sm:flex flex-col gap-0.5 min-w-0" aria-hidden>
                      {items.slice(0, 2).map((it) => (
                        <span key={it.id} className={`truncate rounded px-1 text-[11px] font-medium ${CHIP[it.kind]}`}>
                          {it.title}
                        </span>
                      ))}
                      {items.length > 2 ? (
                        <span className="px-1 text-[11px] text-muted">{t('calendar.more', { count: items.length - 2 })}</span>
                      ) : null}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 p-3 text-xs text-muted">
              <span className="font-semibold">{t('calendar.legend')}</span>
              {KINDS.map((k) => (
                <span key={k} className="inline-flex items-center gap-1.5">
                  <span className={`h-2 w-2 rounded-full ${DOT[k]}`} aria-hidden />
                  {t(`calendar.kind.${k}` as TranslationKey)}
                </span>
              ))}
            </div>
          </Card>

          <Card>
            <div className="flex items-start gap-2 mb-3">
              <div className="flex-1 min-w-0">
                <h2 className="font-bold">{dateLong(selected)}</h2>
                <p className="text-xs text-muted">
                  {t(dayItems.length === 1 ? 'calendar.itemsOne' : 'calendar.itemsMany', { count: dayItems.length })}
                </p>
              </div>
              {canAdd ? (
                <button type="button" className="btn-secondary" onClick={() => setCreating(true)}>
                  {t('calendar.addForDay')}
                </button>
              ) : null}
            </div>

            {dayItems.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted">{t('calendar.nothing')}</p>
            ) : (
              <ul className="space-y-2">
                {dayItems.map((item) => (
                  <li key={item.id}>
                    <AgendaItem item={item} allowLink={(s) => can(s, 'read')} onEdit={() => setEditing(item)} />
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}
    </>
  );
}

function AgendaItem({
  item,
  allowLink,
  onEdit,
}: {
  item: CalendarItem;
  allowLink: (section: Section) => boolean;
  onEdit: () => void;
}) {
  const { t } = useI18n();
  const span = itemSpan(item);
  const multiDay = span.last > span.first;

  const sourceKey = (item.kind === 'appointment' ? null : `calendar.source.${item.source}`) as TranslationKey | null;
  const sourceLabel = sourceKey ? t(sourceKey) : null;
  // An unknown source would print its own key path; better to show nothing.
  const showSource = sourceLabel !== null && sourceLabel !== sourceKey;

  const when = item.allDay
    ? t('calendar.allDay')
    : `${time(item.start)}${item.end ? `–${time(item.end)}` : ''}`;

  const section = item.link ? sectionForLink(item.link) : null;
  const linkable = item.link !== null && (section === null || allowLink(section));
  // The API sends the literal word "pending" as a subtitle for a request awaiting a
  // decision. The badge already says it, in the right language.
  const subtitle = item.subtitle && item.subtitle !== 'pending' ? item.subtitle : null;

  return (
    <div className="relative flex gap-3 rounded border border-border bg-surface ps-4 pe-3 py-2.5 overflow-hidden">
      <span className={`absolute start-0 top-0 bottom-0 w-1 ${BAR[item.kind]}`} aria-hidden />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge tone={item.kind === 'leave' && item.source === 'pending' ? 'neutral' : KIND_TONE[item.kind]}>
            {t(`calendar.kind.${item.kind}` as TranslationKey)}
          </Badge>
          {showSource ? <span className="text-xs text-muted">{sourceLabel}</span> : null}
        </div>

        <p className="text-sm font-semibold break-words">
          {linkable && item.link ? (
            <Link to={item.link} className="text-primary hover:underline">
              {item.title}
            </Link>
          ) : (
            item.title
          )}
        </p>

        <p className="text-xs text-muted tabular">
          {when}
          {multiDay ? ` · ${t('calendar.until', { date: dateShort(span.last) })}` : ''}
        </p>

        {subtitle ? <p className="text-xs text-muted break-words">{subtitle}</p> : null}
        {item.description ? <p className="text-sm whitespace-pre-line break-words">{item.description}</p> : null}

        {item.kind === 'appointment' ? (
          <p className="text-xs text-muted break-words">
            {item.attendees && item.attendees.length > 0
              ? `${t('calendar.attendees')}: ${item.attendees.map((a) => a.name).join(', ')}`
              : t('calendar.everyone')}
          </p>
        ) : null}
      </div>

      {item.canEdit ? (
        <button type="button" className="btn-secondary self-start" onClick={onEdit}>
          {t('common.edit')}
        </button>
      ) : null}
    </div>
  );
}

function Chevron({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg viewBox="0 0 20 20" className="w-4 h-4 rtl:-scale-x-100" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d={direction === 'left' ? 'm12 5-5 5 5 5' : 'm8 5 5 5-5 5'} />
    </svg>
  );
}
